/**
 * AlertCard Component
 *
 * Card component for displaying individual alerts.
 * Shows severity, type, message, and action buttons.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertSeverity, AlertType } from '../services/realtime-alert.service';
import type { Alert } from '../services/realtime-alert.service';
import {
  getSeverityColor,
  getSeverityBgColor,
  formatRelativeTime,
  formatAlertType,
} from '../utils/browser-notifications';
import { logger } from '../utils/logger';

interface AlertCardProps {
  alert: Alert;
  onMarkAsRead?: (alertId: string) => Promise<void>;
  onAcknowledge?: (alertId: string) => Promise<void>;
  onDismiss?: (alertId: string) => Promise<void>;
  showActions?: boolean;
  compact?: boolean;
  onClick?: (alert: Alert) => void;
}

// Severity icons
const SeverityIcon: React.FC<{ severity: AlertSeverity }> = ({ severity }) => {
  const color = getSeverityColor(severity);

  switch (severity) {
    case AlertSeverity.CRITICAL:
      return (
        <svg className="w-5 h-5" fill={color} viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case AlertSeverity.HIGH:
      return (
        <svg className="w-5 h-5" fill={color} viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case AlertSeverity.WARNING:
      return (
        <svg className="w-5 h-5" fill={color} viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill={color} viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
};

// Alert type icon
const AlertTypeIcon: React.FC<{ alertType: AlertType }> = ({ alertType }) => {
  const iconClass = 'w-4 h-4 text-gray-500';

  switch (alertType) {
    case AlertType.SHELL_GAME_DETECTED:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case AlertType.FATAL_CRASH:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case AlertType.CONTACT_MATCH_FOUND:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      );
    case AlertType.OOS_ORDER_ISSUED:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      );
    case AlertType.INSURANCE_EXPIRING:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    case AlertType.AUTHORITY_REVOKED:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
  }
};

const AlertCard: React.FC<AlertCardProps> = ({
  alert,
  onMarkAsRead,
  onAcknowledge,
  onDismiss,
  showActions = true,
  compact = false,
  onClick,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const isUnread = !alert.readAt;
  const isAcknowledged = !!alert.acknowledgedAt;

  // Handle card click
  const handleClick = () => {
    if (onClick) {
      onClick(alert);
    }
  };

  // Handle action with loading state
  const handleAction = async (
    action: ((alertId: string) => Promise<void>) | undefined,
    alertId: string
  ) => {
    if (!action) return;

    setIsLoading(true);
    try {
      await action(alertId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Action failed';
      logger.error('Error performing alert action:', { error, alertId, action: 'alert_action' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view carrier click
  const handleViewCarrier = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (alert.dotNumber) {
      navigate(`/dashboard?dot=${alert.dotNumber}`);
    }
  };

  if (compact) {
    return (
      <div
        onClick={handleClick}
        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
          isUnread ? 'bg-blue-50' : 'bg-white'
        } hover:bg-gray-50 border border-gray-200`}
      >
        <SeverityIcon severity={alert.severity} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm truncate ${isUnread ? 'font-semibold' : ''}`}>
            {alert.title}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatRelativeTime(alert.createdAt)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border transition-all ${
        isUnread ? 'border-l-4 bg-white shadow-md' : 'border-gray-200 bg-white'
      }`}
      style={{
        borderLeftColor: isUnread ? getSeverityColor(alert.severity) : undefined,
      }}
    >
      {/* Header */}
      <div
        className="p-4 cursor-pointer"
        onClick={handleClick}
        style={{ backgroundColor: isUnread ? getSeverityBgColor(alert.severity) : undefined }}
      >
        <div className="flex items-start gap-3">
          {/* Severity Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <SeverityIcon severity={alert.severity} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2">
              <h3 className={`text-base ${isUnread ? 'font-semibold' : ''} text-gray-900`}>
                {alert.title}
              </h3>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatRelativeTime(alert.createdAt)}
              </span>
            </div>

            {/* Message */}
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{alert.message}</p>

            {/* Meta Row */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* Alert Type Badge */}
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
                style={{
                  backgroundColor: `${getSeverityColor(alert.severity)}20`,
                  color: getSeverityColor(alert.severity),
                }}
              >
                <AlertTypeIcon alertType={alert.alertType} />
                {formatAlertType(alert.alertType)}
              </span>

              {/* Severity Badge */}
              <span
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                style={{
                  backgroundColor: `${getSeverityColor(alert.severity)}20`,
                  color: getSeverityColor(alert.severity),
                }}
              >
                {alert.severity}
              </span>

              {/* DOT Number */}
              {alert.dotNumber && (
                <button
                  onClick={handleViewCarrier}
                  className="inline-flex items-center px-2 py-0.5 text-xs text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                >
                  DOT {alert.dotNumber}
                </button>
              )}

              {/* Status Badges */}
              {isAcknowledged && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs text-green-600 bg-green-100 rounded-full">
                  Acknowledged
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <div className="flex items-center gap-2">
            {isUnread && onMarkAsRead && (
              <button
                onClick={() => handleAction(onMarkAsRead, alert.id)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Mark as Read
              </button>
            )}

            {!isAcknowledged && onAcknowledge && (
              <button
                onClick={() => handleAction(onAcknowledge, alert.id)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50"
              >
                Acknowledge
              </button>
            )}

            {onDismiss && (
              <button
                onClick={() => handleAction(onDismiss, alert.id)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                Dismiss
              </button>
            )}

            <div className="flex-1" />

            {/* View Details */}
            {alert.data && Object.keys(alert.data).length > 0 && (
              <button
                onClick={handleClick}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                View Details
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertCard;
